import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ThumbsUp, ThumbsDown, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getArticuloBySlug } from "@/lib/articulos.functions";

export const Route = createFileRoute("/news/$slug")({
  loader: async ({ params }) => {
    const a = await getArticuloBySlug({ data: { slug: params.slug } });
    if (!a) throw notFound();
    return { articulo: a };
  },
  head: ({ params, loaderData }) => {
    const a = loaderData?.articulo;
    const title = a?.seo_title || a?.titulo || "Bizaclia News";
    const desc = a?.seo_description || "";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `/news/${params.slug}` },
        ...(a?.cover_url ? [{ property: "og:image", content: a.cover_url }] : []),
      ],
      links: [{ rel: "canonical", href: `/news/${params.slug}` }],
    };
  },
  component: ArticuloPage,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold">Artículo no encontrado</h1>
      <Link to="/news" className="mt-4 inline-block text-primary underline">
        Volver a Bizaclia News
      </Link>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold">Error</h1>
      <p className="mt-2 text-muted-foreground">{error.message}</p>
    </main>
  ),
});

function ArticuloPage() {
  const { articulo: a } = Route.useLoaderData();

  const onVote = () =>
    toast.message("Inicia sesión para votar", {
      description: "El sistema de cuentas estará disponible pronto.",
    });

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/news"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Bizaclia News
      </Link>

      <article className="mt-6">
        {a.cover_url && (
          <figure className="overflow-hidden rounded-lg">
            <img src={a.cover_url} alt="" className="aspect-video w-full object-cover" />
            {a.cover_credit && (
              <figcaption className="mt-2 text-xs text-muted-foreground">
                {a.cover_credit}
              </figcaption>
            )}
          </figure>
        )}

        <h1 className="mt-6 text-4xl font-bold tracking-tight">{a.titulo}</h1>
        <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
          <span className="capitalize">{a.autor_tipo}</span>
          {a.published_at && (
            <>
              <span>·</span>
              <time dateTime={a.published_at}>
                {new Date(a.published_at).toLocaleDateString("es-ES", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </time>
            </>
          )}
        </div>

        <div className="prose prose-neutral mt-8 max-w-none whitespace-pre-wrap">
          {a.contenido}
        </div>

        <div className="mt-10 flex items-center gap-3 border-t border-border pt-6">
          <button
            onClick={onVote}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent"
          >
            <ThumbsUp className="h-4 w-4" /> {a.likes}
          </button>
          <button
            onClick={onVote}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent"
          >
            <ThumbsDown className="h-4 w-4" /> {a.dislikes}
          </button>
        </div>
      </article>
    </main>
  );
}
